import { EMPTY_PALETTE } from '../../../../constants'
import type {
  HorizonType,
  IWebGlRender,
  Nullable,
  PaletteBody,
  Property,
  QuadPoints,
  ScaleType,
  VertexAttribute,
} from '../../../../types'
import {
  bindAttribute,
  createAttribute,
  getOrCreateProgram,
  getProjectionMatrix,
  getWebGlContext,
  unbindAttribute,
  updateAttributeBuffer,
} from '../../../../utils'
import { createTextureBuffer } from './shaderHelpers'
import { fragmentShaderSource, vertexShaderSource } from './shaders'

type Attributes = 'aPosition' | 'aTextureCoord'

export class HorizonRender implements IWebGlRender, Disposable {
  private canvas: HTMLCanvasElement
  private program: WebGLProgram
  private renderingContext: WebGL2RenderingContext

  private textureBuffer: Nullable<WebGLTexture>

  private attributes: Record<Attributes, VertexAttribute>

  private projectionLocation: Nullable<WebGLUniformLocation>
  private imageUniform: Nullable<WebGLUniformLocation>
  private nullValueUniform: Nullable<WebGLUniformLocation>
  private usePaletteUniform: Nullable<WebGLUniformLocation>
  private paletteUniform: Nullable<WebGLUniformLocation>
  private scaleUniform: Nullable<WebGLUniformLocation>
  private offsetUniform: Nullable<WebGLUniformLocation>

  private nullValue: number
  private coordinates: QuadPoints
  private palette: PaletteBody
  private zoom: ScaleType
  private scale: number
  private offset: number

  private isDisposed: boolean

  constructor(
    canvas: HTMLCanvasElement,
    programs: Property<Record<string, WebGLProgram>>,
    zoom: ScaleType,
    coordinates: QuadPoints,
    headerNullValue: number,
    imageBody: HorizonType['body'],
    palette: Nullable<PaletteBody>,
    attributeParams: [number, number],
  ) {
    this.canvas = canvas
    this.zoom = zoom
    this.coordinates = coordinates
    this.nullValue = headerNullValue
    this.palette = palette ?? EMPTY_PALETTE
    this.scale = attributeParams[0]
    this.offset = attributeParams[1]

    this.textureBuffer = null

    const gl = getWebGlContext(canvas)
    this.renderingContext = gl

    const program = getOrCreateProgram(programs, gl, vertexShaderSource, fragmentShaderSource)
    this.program = program

    this.attributes = {
      aPosition: createAttribute(gl, program, 'aPosition', 2, gl.FLOAT),
      aTextureCoord: createAttribute(gl, program, 'aTextureCoord', 2, gl.FLOAT),
    }

    this.projectionLocation = gl.getUniformLocation(program, 'uProjection')
    this.imageUniform = gl.getUniformLocation(program, 'uImage')
    this.nullValueUniform = gl.getUniformLocation(program, 'uNullValue')
    this.usePaletteUniform = gl.getUniformLocation(program, 'uUsePalette')
    this.paletteUniform = gl.getUniformLocation(program, 'uPalette[0]')
    this.scaleUniform = gl.getUniformLocation(program, 'uScale')
    this.offsetUniform = gl.getUniformLocation(program, 'uOffset')
    this.setCoordinates(coordinates)
    this.setImageData(imageBody)

    this.isDisposed = false
  }

  public render() {
    if (this.isDisposed) return

    const renderingContext = this.renderingContext

    renderingContext.disable(renderingContext.BLEND)

    renderingContext.useProgram(this.program)

    Object.entries(this.attributes).forEach(attr => {
      bindAttribute(renderingContext, attr[1])
    })

    renderingContext.activeTexture(renderingContext.TEXTURE0)
    renderingContext.bindTexture(renderingContext.TEXTURE_2D, this.textureBuffer)
    renderingContext.uniform1i(this.imageUniform, 0)
    renderingContext.uniform1f(this.nullValueUniform, this.nullValue)
    renderingContext.uniform1f(this.scaleUniform, this.scale)
    renderingContext.uniform1f(this.offsetUniform, this.offset)

    renderingContext.uniformMatrix3fv(
      this.projectionLocation,
      false,
      getProjectionMatrix(this.canvas, this.zoom),
    )
    renderingContext.uniform1i(this.usePaletteUniform, this.palette === EMPTY_PALETTE ? 0 : 1)
    renderingContext.uniform4fv(this.paletteUniform, this.palette)

    renderingContext.drawArrays(renderingContext.TRIANGLES, 0, 6)

    Object.entries(this.attributes).forEach(attr => {
      unbindAttribute(renderingContext, attr[1])
    })
  }

  public setZoomState(zoom: ScaleType) {
    this.zoom = zoom
  }

  public setCoordinates(points: QuadPoints) {
    this.coordinates = points
    updateAttributeBuffer(
      this.renderingContext,
      this.attributes['aPosition'],
      new Float32Array([
        points[3].x,
        points[3].y,
        points[2].x,
        points[2].y,
        points[0].x,
        points[0].y,
        points[0].x,
        points[0].y,
        points[2].x,
        points[2].y,
        points[1].x,
        points[1].y,
      ]),
    )

    const [u1, v1, u2, v2] = [0.0, 1.0, 1.0, 0.0]

    updateAttributeBuffer(
      this.renderingContext,
      this.attributes['aTextureCoord'],
      new Float32Array([u1, v1, u2, v1, u1, v2, u1, v2, u2, v1, u2, v2]),
    )
  }

  public setImageData(imageBody: HorizonType['body']) {
    this.textureBuffer = createTextureBuffer(this.renderingContext, imageBody)
  }

  public setAttributeParams(attributeParams: [number, number]) {
    this.scale = attributeParams[0]
    this.offset = attributeParams[1]
    return this
  }
  public setPalette(palette: PaletteBody) {
    this.palette = palette
    return this
  }

  [Symbol.dispose](): void {
    const renderingContext = this.renderingContext
    renderingContext.deleteTexture(this.textureBuffer)
    Object.entries(this.attributes).forEach(attr => {
      renderingContext.deleteBuffer(attr[1].buffer)
    })
    this.isDisposed = true
  }
}
